import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronUp } from "lucide-react";
import { API } from "../../App";

const EXAM_LABEL = { caderno_1: "Caderno I", caderno_2: "Caderno II", comunicacoes: "Comunicações" };
const RESULT_LABEL = { agendado: "Marcado", aprovado: "Aprovado", reprovado: "Reprovado", faltou: "Faltou", justificado: "Justificado (ANAC)" };
const RESULT_CLASS = {
  agendado: "text-sky-300 border-sky-500/40",
  aprovado: "text-emerald-300 border-emerald-500/40",
  reprovado: "text-red-300 border-red-500/40",
  faltou: "text-red-300 border-red-500/40",
  justificado: "text-amber-300 border-amber-500/40",
};
// Época = mês de exame, em formato numérico: MM/AAAA (por exemplo 09/2026)
const epochLabel = (iso) => {
  const [y, m] = iso.split("-");
  return `${m}/${y}`;
};
const dayLabel = (iso) => iso.split("-").reverse().join("/");

const EXAM_ORDER = ["caderno_1", "caderno_2", "comunicacoes"];

// Estado de um caderno para o card: aprovado, caducado, marcado, sem tentativas, reprovado, faltou ou por fazer
const cardStatus = (kind, usage, records) => {
  const ex = usage.exams[kind];
  const mine = records
    .filter((r) => r.exam_kind === kind)
    .sort((a, b) => String(a.exam_date).localeCompare(String(b.exam_date)));
  const last = mine[mine.length - 1];
  if (ex.approved) return { label: "Aprovado", cls: "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" };
  if (ex.expired) return { label: "Caducado: repetir", cls: "border-amber-500/50 bg-amber-500/10 text-amber-300" };
  if (mine.some((r) => r.result === "agendado")) return { label: "Marcado", cls: "border-sky-500/50 bg-sky-500/10 text-sky-300" };
  if (ex.attempts_used >= ex.attempts_max) return { label: "Sem tentativas", cls: "border-red-500/50 bg-red-500/10 text-red-300" };
  if (last && last.result === "reprovado") return { label: "Reprovado: repetir", cls: "border-red-500/50 bg-red-500/10 text-red-300" };
  if (last && last.result === "faltou") return { label: "Faltou", cls: "border-red-500/50 bg-red-500/10 text-red-300" };
  return { label: "Por fazer", cls: "border-slate-600 bg-slate-800/60 text-slate-400" };
};

// Página inicial do aluno: próximo exame (ANAC ou prático) e as suas teóricas. Só leitura.
export default function AnacStudentPanel() {
  const [data, setData] = useState(null);
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/anac-theory/me`, { withCredentials: true });
        if (!cancel) setData(r.data);
      } catch { /* sem dados: a secção não aparece */ }
    })();
    return () => { cancel = true; };
  }, []);

  if (!data) return null;
  const next = data.next_exam;
  const aprovados = EXAM_ORDER.filter((k) => data.usage.exams[k].approved).length;
  const resumo = data.usage.all_approved
    ? "Teóricas completas"
    : `${aprovados} de ${EXAM_ORDER.length} cadernos aprovados${next ? ` · próximo exame ${dayLabel(next.date)}` : ""}`;

  return (
    <section className="mb-3" data-testid="anac-student-panel">
      <button
        type="button"
        onClick={() => setAberto(!aberto)}
        aria-expanded={aberto}
        className={`w-full text-left ${aberto ? "mb-3" : ""} rounded-lg border border-slate-700 bg-slate-900/50 p-3 sm:p-4 flex items-center justify-between gap-3`}
        data-testid="anac-toggle"
      >
        <div className="min-w-0">
          <div className="font-semibold text-slate-100">Teóricas ANAC</div>
          <div className="text-xs text-slate-400 break-words">{resumo}</div>
        </div>
        {aberto
          ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
          : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />}
      </button>
      {aberto && (
      <div className="space-y-3">
      {next && (
        <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 sm:p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="anac-next-exam">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-sky-300">Próximo exame</div>
            <div className="text-base font-semibold text-slate-100">
              {dayLabel(next.date)} · {next.label}
            </div>
          </div>
          <div className="text-sm text-slate-300">
            {next.days_left === 0 ? "É hoje" : `Faltam ${next.days_left} dia${next.days_left === 1 ? "" : "s"}`}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 sm:p-4">
        {/* Um card por caderno: sabes de relance o que falta para teóricas completas */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3" data-testid="anac-exam-cards">
          {EXAM_ORDER.map((kind) => {
            const ex = data.usage.exams[kind];
            const st = cardStatus(kind, data.usage, data.records);
            return (
              <div key={kind} className={`min-w-0 break-words rounded-md border p-3 ${st.cls}`} data-testid={`anac-card-${kind}`}>
                <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                  <span className="min-w-0 break-words font-semibold text-slate-100">{EXAM_LABEL[kind]}</span>
                  <span className="min-w-0 break-words text-xs">{st.label}</span>
                </div>
                <div className="min-w-0 break-words text-[11px] text-slate-400 mt-1">Tentativas: {ex.attempts_used} de {ex.attempts_max}</div>
              </div>
            );
          })}
        </div>
        <div className="text-xs mb-3" data-testid="anac-complete-summary">
          {data.usage.all_approved ? (
            <span className="text-emerald-300">
              Teóricas completas.{data.usage.licence_valid_until ? ` Validade para a licença: ${dayLabel(data.usage.licence_valid_until)}.` : ""}
            </span>
          ) : (
            <span className="text-slate-400">
              Faltam {EXAM_ORDER.filter((k) => !data.usage.exams[k].approved).length} de 3 cadernos para teóricas completas.
            </span>
          )}
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="font-semibold text-slate-100">As minhas teóricas ANAC</div>
          <div className="text-xs text-slate-400">Sittings: {data.usage.seats_used} de {data.usage.seats_max}</div>
        </div>

        {data.records.length === 0 ? (
          <div className="text-xs text-slate-500 italic">Ainda sem exames ANAC registados. O instrutor regista as datas e os resultados.</div>
        ) : (
          <div>
            {/* Cabeçalho só em ecrãs largos; em telemóvel cada exame ocupa duas linhas */}
            <div className="hidden sm:grid grid-cols-[1.3fr_0.9fr_1fr_0.6fr_1.3fr] gap-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
              <span>Caderno</span>
              <span className="text-right">Época</span>
              <span>Data</span>
              <span className="text-right">Nota</span>
              <span className="text-right">Resultado</span>
            </div>
            {data.records.map((rec) => (
              <div
                key={rec.id}
                className="grid grid-cols-[1fr_auto] sm:grid-cols-[1.3fr_0.9fr_1fr_0.6fr_1.3fr] gap-x-3 gap-y-1 items-center py-2 border-t border-slate-800 first:border-t-0 text-sm"
                data-testid="anac-student-record"
              >
                <span className="text-slate-200">{EXAM_LABEL[rec.exam_kind] || rec.exam_kind}</span>
                <span className="hidden sm:block text-xs text-slate-300 text-right">{epochLabel(rec.exam_date)}</span>
                <span className="hidden sm:block font-mono text-xs text-slate-400">{dayLabel(rec.exam_date)}</span>
                <span className="hidden sm:block font-mono text-xs text-slate-300 text-right">{rec.score != null ? `${rec.score}%` : "—"}</span>
                <span className="justify-self-end">
                  <span className={`text-xs px-2 py-0.5 rounded border whitespace-nowrap ${RESULT_CLASS[rec.result]}`}>{RESULT_LABEL[rec.result]}</span>
                </span>
                <span className="sm:hidden col-span-2 text-xs text-slate-400">
                  Época {epochLabel(rec.exam_date)} · {dayLabel(rec.exam_date)}{rec.score != null ? ` · nota ${rec.score}%` : ""}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="mt-3 space-y-1 text-xs text-slate-300 leading-relaxed">
          {data.usage.theory_deadline && (
            <div data-testid="anac-student-deadline">
              Prazo para completar as provas: {dayLabel(data.usage.theory_deadline)}
              {data.usage.expired && <span className="text-red-300"> · ultrapassado: tem de repetir as provas aprovadas</span>}
            </div>
          )}
          {data.usage.licence_valid_until && (
            <div>Validade das provas para a licença: {dayLabel(data.usage.licence_valid_until)}</div>
          )}
          {data.course && (
            <div data-testid="anac-student-course">
              Fim do prazo do curso (2 anos): {dayLabel(data.course.deadline)} · Cartão de aluno válido até: {data.cartao_aluno_valid_until ? dayLabel(data.cartao_aluno_valid_until) : "não registado"}
            </div>
          )}
        </div>
        <div className="text-[11px] text-slate-500 mt-2">Aprovado com 75% ou mais. Só o instrutor regista e corrige.</div>
      </div>
      </div>
      )}
    </section>
  );
}
